import { Link } from "react-router-dom";
import { Server, TestTube2, Briefcase, Sparkles, Lightbulb, CheckCircle2, ArrowUpRight } from "lucide-react";

const servicesDetailed = [
  {
    id: "managed-services",
    icon: Server,
    eyebrow: "01",
    title: "Managed Services & Support (BSS)",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2000&auto=format&fit=crop",
    desc: "We run the business support stack behind your operator — billing, CRM, mediation, charging, partner management — with a senior on-call team and a disciplined incident framework.",
    subdesc: "Our senior engineers handle the complexity so your team can focus on growth.",
    bullets: [
      "L1–L3 support with committed response SLAs",
      "Billing, CRM, mediation & charging operations",
      "Revenue assurance and leakage detection",
      "Automation of manual BSS workflows",
    ],
  },
  {
    id: "testing",
    icon: TestTube2,
    eyebrow: "02",
    title: "End-to-End Testing (Pre-ATP, ATP, UAT)",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=2000&auto=format&fit=crop",
    desc: "Rigorous validation of every integration across the BSS/OSS estate — before launch and after every major change — so your revenue path never breaks in production.",
    subdesc: "Catching critical defects before they impact your live subscriber base.",
    bullets: [
      "Pre-ATP shake-down and environment readiness",
      "ATP execution with operator sign-off protocols",
      "UAT support, traceability and defect triage",
      "Regression automation and test-data management",
    ],
  },
  {
    id: "project-management",
    icon: Briefcase,
    eyebrow: "03",
    title: "Project Management",
    image: "https://images.unsplash.com/photo-1454165833762-0265129b0021?q=80&w=2000&auto=format&fit=crop",
    desc: "PMO discipline for telecom rollouts — planning, execution, delivery timelines, risk governance and stakeholder alignment, run by consultants who've shipped into live networks.",
    subdesc: "Disciplined execution and risk mitigation for complex network rollouts.",
    bullets: [
      "Program and project governance (PMO setup)",
      "Vendor and integrator coordination",
      "Risk, issue and dependency management",
      "Milestone tracking and executive reporting",
    ],
  },
  {
    id: "brand-product",
    icon: Sparkles,
    eyebrow: "04",
    title: "Brand & Product Development",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?q=80&w=2000&auto=format&fit=crop",
    desc: "Telecom product positioning and go-to-market design — so new services and pricing plans launch ready for market, not as an afterthought to the technical rollout.",
    subdesc: "Bridging the gap between technical capability and market demand.",
    bullets: [
      "Product naming, positioning and messaging",
      "Pricing design and plan architecture",
      "Launch collateral and GTM playbooks",
      "Market-ready service catalog definitions",
    ],
  },
  {
    id: "consulting",
    icon: Lightbulb,
    eyebrow: "05",
    title: "Telecom Consulting Strategy",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2000&auto=format&fit=crop",
    desc: "Advisory engagements on BSS transformation, digital readiness and operational optimisation — pragmatic recommendations from consultants who've run the stack themselves.",
    subdesc: "Pragmatic roadmaps built on actual operator experience, not just theory.",
    bullets: [
      "BSS modernisation roadmaps",
      "Operational optimisation and cost-to-serve reviews",
      "Vendor selection and architecture advisory",
      "Digital readiness and experience audits",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-48 pb-20 md:pb-28 text-center">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="section-label">Services</span>
          <h1 className="mt-6 font-semibold tracking-tight leading-[1.08] text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl max-w-[15ch] mx-auto">
            Telecom <span className="gradient-text">Services.</span>
          </h1>
          <p className="mt-6 md:mt-7 text-sm sm:text-base md:text-lg text-gray-400 max-w-[34ch] sm:max-w-xl md:max-w-2xl mx-auto leading-relaxed">
            Five practices, one accountability line. Pick the engagement model that fits — or combine them for a fully managed delivery.
          </p>
        </div>
      </section>

      {/* Detailed services */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-8 md:space-y-10">
          {servicesDetailed.map((s) => (
            <div
              key={s.id}
              id={s.id}
              data-testid={`service-detailed-${s.id}`}
              className="card-base p-8 md:p-12 grid md:grid-cols-12 gap-10 items-stretch scroll-mt-24"
            >
              <div className="md:col-span-5 flex flex-col">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="icon-circle w-16 h-16"><s.icon className="w-6 h-6" /></div>
                    <span className="text-xs font-semibold tracking-[0.25em] text-gray-500">/ {s.eyebrow}</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl font-medium tracking-tight leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
                
                <div className="mt-8 pt-8 border-t border-white/5">
                  <p className="text-sm text-gray-500 italic leading-relaxed">
                    "{s.subdesc}"
                  </p>
                </div>

                <div className="mt-auto pt-10">
                  <Link to="/contact" className="btn-primary w-full flex justify-center py-4 text-base font-bold">
                    Talk to a specialist <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>

              <div className="md:col-span-7 md:border-l md:border-white/5 md:pl-10">
                <div className="relative mb-8 rounded-md overflow-hidden aspect-[16/8] group border border-white/5 shadow-2xl">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/80 via-transparent to-transparent opacity-60" />
                </div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-cyan-400 mb-5">What's included</p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-gray-300">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative card-base overflow-hidden p-12 md:p-16 text-center">
            <div className="absolute inset-0 grid-pattern opacity-40" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Need a partner across more than one of <span className="gradient-text">these?</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-10 leading-relaxed">
                We run fully managed programs that combine testing, delivery and ongoing support — with a single point of accountability.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Start a Scoping Call <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/" className="btn-ghost">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
